import type { DealParty } from '@/services/dealParties'

export const PARTY_GROUPS = [
  'Borrower & Sponsor',
  'Intermediaries',
  'Internal Team',
  'Additional Parties',
] as const
export function partyGroup(party: DealParty): string {
  if (party.isInternal || party.kind === 'Internal team') return PARTY_GROUPS[2]
  if (/broker|intermediar/i.test(party.kind)) return PARTY_GROUPS[1]
  // A party the roster sent as "Other party" whose recorded role is on the
  // sponsor's side (the sponsor's own "Sponsor contact") is filed with the
  // sponsor, not among additional parties (Ashby Commons, Wren Hollow).
  const sponsorSide = /sponsor|borrower|borrowing|guarantor|principal/i
  if (sponsorSide.test(party.kind) || (party.roles ?? []).some((r) => sponsorSide.test(r)))
    return PARTY_GROUPS[0]
  return PARTY_GROUPS[3]
}
export function partyColor(party: DealParty): string {
  return (
    (
      {
        'Borrower & Sponsor': '#d9b768',
        Intermediaries: '#64b7ee',
        'Internal Team': '#58c68b',
      } as Record<string, string>
    )[partyGroup(party)] ?? '#aeb6bf'
  )
}
/** The name a card is titled by. An organisation is titled by its own name;
 *  its recorded contact is a person at it, shown beneath (`entityContact`).
 *  Titling it by the contact typed a person as an Organization and hid the
 *  sponsor from the roster (Stanhope Row: "Ivan Roarke · Organization"). */
export function partyName(party: DealParty): string {
  if (party.isEntity) return party.name || party.contact || ''
  return party.contact || party.name
}
/** An organisation's recorded contact person, when it names somebody other
 *  than the organisation itself. */
export function entityContact(party: DealParty): string | null {
  if (!party.isEntity || !party.contact) return null
  const fold = (value?: string | null) => (value ?? '').trim().toLowerCase()
  const contact = fold(party.contact)
  return contact === fold(party.name) || contact === fold(party.company) ? null : party.contact
}
export function partyRoles(party: DealParty): string[] {
  // One entity can hold several roles on a deal (the sponsor that is also the
  // guarantor); the roster sends them all.
  if (party.roles?.length) return [...new Set(party.roles)]
  // Correspondence capacities (e.g. Copied) are evidence, not deal roles.
  const roles = party.facts
    .filter(([key]) => /^(deal role|role|roles)$/i.test(key))
    .map(([, value]) => value)
  return [...new Set(roles.length ? roles : [party.kind])]
}
/** Whether this entry is filed under a filter's party type. */
export function partyHasKind(party: DealParty, kind: string): boolean {
  return party.kind === kind || (party.kinds ?? []).includes(kind)
}

const LEGAL_SUFFIXES = new Set([
  'llc', 'lllp', 'llp', 'lp', 'inc', 'incorporated', 'corp', 'corporation',
  'ltd', 'limited', 'co', 'company', 'plc', 'pllc', 'pc', 'na',
])
/** An organisation's name as it compares when the roster sent no key:
 *  case, punctuation, "&"/"and" and the legal suffix folded away. Mirrors
 *  `party_identity.entity_key` for the spellings the map has to join. */
export function orgKeyOf(name: string): string {
  const tokens = name
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/\bl\.?\s*l\.?\s*c\.?/g, ' llc ')
    .replace(/\bl\.?\s*p\.?(?=\s|$)/g, ' lp ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
  while (tokens.length > 1 && LEGAL_SUFFIXES.has(tokens[tokens.length - 1])) tokens.pop()
  return `org:${tokens.join(' ')}`
}
/** A person's name as it compares, for joining a contact to a roster entry. */
export function personKeyOf(name: string): string {
  return `person:${name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()}`
}
/** The key of the entity this roster entry IS. */
export function entityKeyOf(party: DealParty): string {
  if (party.partyKey) return party.partyKey
  return party.isEntity ? orgKeyOf(party.name) : personKeyOf(partyName(party))
}
/** The key of the organisation this entry's person works at, if recorded. */
export function companyKeyOf(party: DealParty): string | null {
  if (party.companyKey) return party.companyKey
  return party.company ? orgKeyOf(party.company) : null
}
export function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase() || '—'
  )
}
export function humanDate(iso?: string | null): string {
  if (!iso) return 'Not on record'
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? 'Unknown date'
    : date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
}
export function fact(party: DealParty, label: string): string {
  return (
    party.facts.find(
      ([key]) => key.toLowerCase() === label.toLowerCase(),
    )?.[1] || 'Not on record'
  )
}
export function safeWebUrl(value?: string | null): string | undefined {
  if (!value) return undefined
  try {
    const url = new URL(value.includes(':') ? value : `https://${value}`)
    return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined
  } catch {
    return undefined
  }
}
export const SCREENING_CHECKS = [
  'Litigation history',
  'Sanctions',
  'Regulatory actions',
  'Credit risk',
  'Adverse media',
  'Identity / KYC',
  'PEP status',
]
